/* eslint-disable max-len */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ClassSpreadBenchmarkResult,
  ClassSpreadReport
} from '@shared/branded-reports/branded-class-spread-report-detail/class-spread-report-detail.model';
import {
  AssessmentStatus
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { ProgressReportType } from '@shared/enums/progress-report-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { TopicType } from '@shared/enums/topic-type';
import { ScoreDetailModalComponent } from '@shared/modals/score-detail-modal/score-detail-modal.component';
import { catchAndRedirectNonErrorToMainFlow } from '@shared/zb-rxjs-operators/rxjs-operators';
import { BehaviorSubject, from, mergeMap, Subscription } from 'rxjs';
import { BrandedReportsHelpers } from '../branded-reports-helpers';
import { BarColors, BorderColors, BorderStyles, TextColors } from '../zbchart-report/bar-colors.enum';
import { ZbChartReportTableComponent } from '../zbchart-report/zbchart-report-table.component';
import { ZbChartBar, ZbChartBarValue, ZbChartData } from '../zbchart-report/zbchart-report.model';
/* eslint-enable max-len */

@Component({
  selector: 'zbp-branded-class-spread-report-detail',
  imports: [
    ZbChartReportTableComponent,
  ],
  templateUrl: './branded-class-spread-report-detail.component.html',
  styleUrl: './branded-class-spread-report-detail.component.scss'
})
export class BrandedClassSpreadReportDetailComponent implements OnChanges, OnInit {

  @Input() showProgressReportsHelp: boolean = false;
  @Input({ required: true }) productName: string;
  @Input({ required: true }) className: string;
  @Input({ required: true }) classSpreadReport?: ClassSpreadReport;
  @Input({ required: true }) variant: string;
  @Input() isPrinting: boolean;
  @Input() printableReportHeadings: IPrintableReportHeading[];
  @Output() refreshData = new EventEmitter();

  zbChart: ZbChartData;

  private reports$ = new BehaviorSubject<ClassSpreadReport>(null);

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private ngbModal: NgbModal,
    private sanitizer: DomSanitizer,
  ) {
  }

  ngOnInit() {
    this.subscriptions.add(
      this.reports$.subscribe((report) => {
        this.createZbChart(report);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.classSpreadReport) {
      this.reports$.next(changes.classSpreadReport.currentValue);
    }

    /** When this input changes, the print function is executed */
    if (changes.isPrinting) {
      if (this.isPrinting) {
        this.print();
      }
    }
  }

  createZbChart(report: ClassSpreadReport): ZbChartData {
    if (!report) {
      return null;
    }

    const chartData = { bars: [] } as ZbChartData;
    chartData.showZeroCount = true;
    const legend = [];

    legend.push({
      label: 'below level',
      color: BarColors.red300,
      id: 'below',
    });
    legend.push({
      label: 'on level',
      color: BarColors.cream300,
      id: 'onLevel',
    });
    legend.push({
      label: 'above level',
      color: BarColors.green300,
      id: 'aboveLevel',
    });
    legend.push({
      label: 'in progress',
      color: BarColors.neutralsWarmGrey400,
      id: 'inProgress',
    });
    legend.push({
      label: 'not assigned',
      color: BarColors.neutralsWarmGrey200,
      id: 'notAssigned',
    });
    legend.push({
      label: 'assigned and not started',
      color: BarColors.white,
      id: 'assignedAndNotStarted',
    });

    chartData.legend = legend;

    report.benchmarkResults.forEach((result) => {
      const row = {} as ZbChartBar;
      const values = [] as ZbChartBarValue[];

      values.push(this.benchmarkToZbBarDataItemOption(result));

      row.label = `${result.lastName}, ${result.firstName}`;
      row.totalCount = result.assessmentStatus === AssessmentStatus.Completed ? result.totalQuestions : null;
      row.values = values;
      row.fullLengthButton = result.assessmentStatus !== AssessmentStatus.Completed;
      row.labelOnLeft = result.assessmentStatus === AssessmentStatus.Completed;
      row.tooltip = this.sanitizer.bypassSecurityTrustHtml(BrandedReportsHelpers.zbChartTooltipFormatter(row));
      row.ariaLabel = `Bar, ${values[0].barLabel}`;
      row.onClick = () => this.onChartClick(result);
      chartData.bars.push(row);
    });

    this.zbChart = chartData;

    return chartData;
  }

  private benchmarkToZbBarDataItemOption(result: ClassSpreadBenchmarkResult): ZbChartBarValue {
    if (result.assessmentStatus === AssessmentStatus.Completed) {
      return {
        tooltipLabel: '',
        barLabel: result.score,
        percent: result.scorePercentage,
        tooltipValue: result.scorePercentage,
        color: this.getBackgroundColor(result.benchmark),
        borderColor: this.getBorderColor(result.benchmark),
        count: result.totalCorrect,
      } as ZbChartBarValue;
    }
    if (result.assessmentStatus === AssessmentStatus.NotStarted) {
      return {
        tooltipLabel: '',
        barLabel: 'NOT STARTED',
        color: BarColors.white,
        borderColor: BorderColors.borderForWhite,
      } as ZbChartBarValue;
    }
    if (result.assessmentStatus === AssessmentStatus.AddScore) {
      return {
        tooltipLabel: '',
        barLabel: '+ ADD SCORE',
        barTextColor: TextColors.brandPrimary,
        borderStyle: BorderStyles.dashed,
        color: BarColors.white,
        borderColor: BorderColors.borderForWhite,
      } as ZbChartBarValue;
    }
    if (result.assessmentStatus === AssessmentStatus.PartiallyScored) {
      return {
        tooltipLabel: '',
        barLabel: '+ PARTIALLY SCORED',
        barTextColor: TextColors.brandPrimary,
        borderStyle: BorderStyles.dashed,
        color: BarColors.white,
        borderColor: BorderColors.borderForWhite,
      } as ZbChartBarValue;
    }
    if (result.assessmentStatus === AssessmentStatus.InProgress) {
      return {
        tooltipLabel: '',
        barLabel: 'TEST IS CURRENTLY IN PROGRESS',
        color: BarColors.neutralsWarmGrey400,
        borderColor: BorderColors.borderForNeutralsWarmGrey400,
      } as ZbChartBarValue;
    }
    return {} as ZbChartBarValue;
  }

  private getBackgroundColor(benchmark: ScoreBenchmark): BarColors {
    switch (benchmark) {
    case ScoreBenchmark.DoesNotMeet:
      return BarColors.red300;
    case ScoreBenchmark.Meets:
      return BarColors.cream300;
    case ScoreBenchmark.Exceeds:
      return BarColors.green300;
    default:
      return BarColors.white;
    }
  }

  private getBorderColor(benchmark: ScoreBenchmark): BorderColors {
    switch (benchmark) {
    case ScoreBenchmark.DoesNotMeet:
      return BorderColors.borderForRed300;
    case ScoreBenchmark.Meets:
      return BorderColors.borderForCream300;
    case ScoreBenchmark.Exceeds:
      return BorderColors.borderForGreen300;
    default:
      return BorderColors.borderForWhite;
    }
  }

  onChartClick(result: ClassSpreadBenchmarkResult) {
    const modalRefObservable = from(ScoreDetailModalComponent.openAssessment(this.ngbModal, {
      activityName: this.classSpreadReport.assessmentName,
      className: this.className,
      productName: this.productName,
      score: result.score,
      studentName: `${result.firstName} ${result.lastName}`,
      topicType: TopicType.Assessment,
      parts: result.scoreDetail as any, // NOT a typo
      benchmark: result.benchmark,
      productType: this.classSpreadReport.productType,
      variant: this.variant,
      classroomId: this.classSpreadReport.classroomId,
      studentId: result.userId,
      assessmentKey: this.classSpreadReport.assessmentKey,
      scoreIsOverridden: result.scoreIsOverridden,
      assessmentStatus: result.assessmentStatus,
      reportName: ProgressReportType.ClassSpread,
    })).pipe(
      mergeMap(ref => ref.result),
      catchAndRedirectNonErrorToMainFlow(),
    );

    this.subscriptions.add(modalRefObservable.subscribe((res) => {
      if (res?.refreshData) {
        this.refreshData.emit();
      }
    }));
  }

  /**
   * Prints report.
   */
  print() {
    // Give time for Printable Header to render
    setTimeout(() => {
      window.print();
    }, 0);
  }
}
